import { ModuleWrapper } from '@/components'
import type { ToggleIconProps } from '@/components_deprecated'
import { IconMode, Toggle } from '@/components_deprecated'
import { PageType } from '@/modules/common/consts'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import type { YearMonthExportData } from '@/modules/exports'
import { ExportType } from '@/modules/exports'
import {
  asBooleanOrDefault,
  asDateOrDefault,
  asEnumValue,
  asNullableString,
} from '@/utils'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useCompany,
  useInitializeUserFilter,
  useNodes,
  useLeaderGroups,
  useModuleConfig,
  useOnErrorAuto,
  useParseQueryState,
  usePermissions,
  useParam,
  useQueryFieldValue,
  useUser,
} from '@/utils/hooks'
import { useUserQuery } from '@/utils/hooks/useUser'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  formatISO,
  getWeek,
  isValid,
  parseISO,
  setDate,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import z from 'zod'
import type { Column } from '../../components_deprecated/Table'
import {
  ExportModal,
  SalaryTimeEntriesTable,
  TimesheetsTable,
} from './components'
import { TimesheetControls } from './components/TimesheetControls'
import { TimeEntryViewEnum, timeEntryViewValues } from './consts'
import { useTimesheetExportMutations } from './mutations.hooks'
import { TIMESHEETS_REPORT_QUERY, USER_TIME_ENTRIES_QUERY } from './queries'
import type { ExportForm, TimesheetFilterForm } from './types'

const Weeks = styled.div`
  h2 {
    margin: 1.5rem 0 1rem 8px;
    font-weight: 600;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const iconProps: Omit<ToggleIconProps, 'icon'> = {
  size: '1.4rem',
}

interface TimeEntriesProps {}

const TimeEntriesFiltersSchema = z.object({
  view: z.enum(timeEntryViewValues),
})

/**
 * Reads an optional `month` query param (e.g. when linked from Activities)
 * and returns it as a Date, or undefined when absent/invalid.
 */
function getMonthParam(): Date | undefined {
  const raw = new URLSearchParams(window.location.search).get('month')
  if (!raw) return undefined
  const date = parseISO(raw)
  return isValid(date) ? date : undefined
}

function useInitialFilters(): TimesheetFilterForm {
  const me = useUser()
  const parsed = useParseQueryState(TimeEntriesFiltersSchema, 'view')
  const viewParam = new URLSearchParams(window.location.search).get('view')
  const legacyView =
    viewParam === 'table'
      ? TimeEntryViewEnum.monthly
      : viewParam === 'week'
      ? TimeEntryViewEnum.weekly
      : undefined

  return {
    user: me.id,
    leaderGroup: undefined,
    month: getMonthParam() ?? new Date(),
    orderBy: 'start',
    view: parsed.view ?? legacyView ?? TimeEntryViewEnum.monthly,
    salaryMode: false,
  }
}

export const TimeEntries: React.FC<TimeEntriesProps> = () => {
  const translations = useTranslate({
    timeEntries: 'common.time-entries',
    weekN: ['common.week-n', { n: 0 }],
    toggle: {
      weekly: 'common.weekly',
      monthly: 'common.monthly',
    },
    dayStart: 'common.day-start',
    dayEnd: 'common.day-end',
    ordinary: 'common.ordinary',
    hours: 'common.work-hours',
    pause: 'common.pause',
    facility: 'common.facility',
    overtime: 'common.overtime',
    date: 'common.date',
    salaryCode: 'common.salary-code',
    department: 'common.department',
    latestAction: 'common.latest-action',

    comment: 'common.comment',
  })

  const company = useCompany()
  const me = useUser()
  const routeUserId = useParam('userId', true)
  // A `month` query param (e.g. from an Activities link) should win over any
  // month persisted in storage when the page initializes.
  const monthParam = getMonthParam()
  const navigate = useNavigate()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const canSeeOthersTimeEntries = hasPermissions(
    PERMISSIONS.timesheets.view.otherstimeentry,
    true
  )
  const onErrorAuto = useOnErrorAuto()

  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [filterStorageStateLoading, setFilterStorageStateLoading] =
    useState(true)

  const {
    formValues: filter,
    updateForm,
    loadStoredFormState,
    updateInitialValues,
  } = useForm<TimesheetFilterForm>({
    values: useInitialFilters(),
    config: {
      callbacks: {
        user: updateUrl,
      },
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'timesheet-filters',
        sessionStorage: true,
        transformValues: {
          user: value =>
            typeof value === 'string' && canSeeOthersTimeEntries
              ? value
              : undefined,
          leaderGroup: asNullableString,
          month: value => asDateOrDefault(value, new Date()),
          view: value =>
            asEnumValue(
              value === 'table'
                ? TimeEntryViewEnum.monthly
                : value === 'week'
                ? TimeEntryViewEnum.weekly
                : value,
              timeEntryViewValues
            ),
          salaryMode: value => asBooleanOrDefault(value, false),
        },
      },
    },
  })

  const { user: routeUser, loading: routeUserLoading } = useUserQuery(
    routeUserId,
    Module.TIMESHEETS
  )

  function updateUrl(id?: string) {
    if (!id) return
    void navigate(`/time-entries/${id}`, { replace: true })
  }

  const applyInitialFilterValues = useCallback(
    (
      values: Partial<TimesheetFilterForm>,
      options?: { runCallbacks?: boolean; runValidation?: boolean }
    ) => {
      updateForm(values, options)
      updateInitialValues(values)
      setFilterStorageStateLoading(false)
    },
    [updateForm, updateInitialValues]
  )

  // Keep Time Entries user filter aligned with route/store precedence.
  useInitializeUserFilter<TimesheetFilterForm>({
    enabled: filterStorageStateLoading,
    routeUserId,
    routeUserLoading,
    routeUserIsValid: !!routeUser?.id,
    fallbackUserId: me.id,
    loadStoredFormState,
    resolveValues: ({ initialUser }) => ({
      values: {
        user: initialUser.userId,
        ...(initialUser.source === 'stored' ? {} : { leaderGroup: null }),
        ...(monthParam ? { month: monthParam } : {}),
      },
      options:
        initialUser.source === 'url'
          ? { runCallbacks: false, runValidation: false }
          : { runValidation: false },
    }),
    applyValues: applyInitialFilterValues,
  })

  useQueryFieldValue('view', filter.view)
  useQueryFieldValue('month', format(filter.month, 'yyyy-MM-dd'))

  const selectedUserId = canSeeOthersTimeEntries ? filter.user ?? me.id : me.id
  const user = useUser(selectedUserId, Module.TIMESHEETS)
  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.TIMESHEETS
  )

  useAutoSelectSingleLeaderGroup({
    canViewOthers: canSeeOthersTimeEntries,
    isAdmin: admin,
    leaderGroup: filter.leaderGroup,
    leaderGroups: myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      const leaderGroupUsers = group.users.map(user => user.id)
      const user =
        filter.user && leaderGroupUsers.includes(filter.user)
          ? filter.user
          : leaderGroupUsers[0] ?? me.id

      updateForm({
        leaderGroup: group.id,
        user,
      })
    },
  })

  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const projectActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_PROJECTS
  )
  const { moduleActive: salaryModuleActive } = useModuleConfig(Module.SALARY)

  const { datetimeStartLt, datetimeEndGte } = useMemo(() => {
    const baseDate = startOfDay(filter.month)
    if (filter.salaryMode && user.salaryWorkTime) {
      const salaryStart = user.salaryWorkTime?.salaryDateStart ?? 1
      const salaryEnd = user.salaryWorkTime?.salaryDateEnd ?? 1

      const forward = 31 - salaryStart > 15

      // What salary period is connected to which month is based on the salary start date.
      if (forward) {
        const datetimeStartLt = setDate(baseDate, salaryStart)
        const datetimeEndGte =
          salaryStart === 1
            ? endOfMonth(baseDate)
            : setDate(addMonths(baseDate, 1), salaryEnd)

        return { datetimeStartLt, datetimeEndGte }
      }

      const datetimeStartLt = setDate(addMonths(baseDate, -1), salaryStart)
      const datetimeEndGte = setDate(baseDate, salaryEnd)

      return { datetimeStartLt, datetimeEndGte }
    } else {
      const datetimeStartLt = startOfMonth(baseDate)
      const datetimeEndGte = endOfMonth(datetimeStartLt)

      return {
        datetimeStartLt,
        datetimeEndGte,
      }
    }
  }, [filter.month, filter.salaryMode, user.salaryWorkTime])

  const queryStartDate = useMemo(
    () => startOfDay(datetimeStartLt),
    [datetimeStartLt]
  )
  const queryEndDate = useMemo(() => endOfDay(datetimeEndGte), [datetimeEndGte])

  const { data, loading } = useQuery(TIMESHEETS_REPORT_QUERY, {
    skip: filterStorageStateLoading || salaryModuleActive,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      userId: selectedUserId,
      startDate: formatISO(datetimeStartLt),
      endDate: formatISO(datetimeEndGte),
      includeWholeWeeks: filter.view === TimeEntryViewEnum.weekly,
    },
    onError: onErrorAuto(),
  })
  const { data: allTimeEntriesData, loading: allTimeEntriesLoading } = useQuery(
    USER_TIME_ENTRIES_QUERY,
    {
      skip: filterStorageStateLoading || !salaryModuleActive,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        user: selectedUserId,
        datetimeStartGt: formatISO(new Date(queryStartDate.getTime() - 1000)),
        datetimeStartLt: formatISO(queryEndDate),
      },
      onError: onErrorAuto(),
    }
  )

  const periodReports = data?.timesheetsReport?.periodReports ?? []
  const salaryWorkTime =
    user.salaryWorkTime ?? data?.timesheetsReport?.salaryWorkTime
  const allTimeEntries = useNodes(allTimeEntriesData?.allTimeEntries)
  const salaryTimeEntries = useMemo(
    () =>
      allTimeEntries
        .filter(entry => {
          const start = parseISO(entry.datetimeStart)
          return start >= queryStartDate && start <= queryEndDate
        })
        .sort(
          (left, right) =>
            new Date(left.datetimeStart).getTime() -
            new Date(right.datetimeStart).getTime()
        ),
    [allTimeEntries, queryEndDate, queryStartDate]
  )
  const weeklySalaryTables = useMemo(() => {
    const groupedEntries = new Map<string, typeof salaryTimeEntries>()

    salaryTimeEntries.forEach(entry => {
      const start = parseISO(entry.datetimeStart)
      const weekKey = format(start, 'yyyy-II')
      const currentWeekEntries = groupedEntries.get(weekKey) ?? []

      groupedEntries.set(weekKey, [...currentWeekEntries, entry])
    })

    return [...groupedEntries.entries()].flatMap(([key, entries]) => {
      const firstEntry = entries[0]
      if (!firstEntry) return []

      return [
        {
          key,
          weekNumber: getWeek(parseISO(firstEntry.datetimeStart)),
          entries,
        },
      ]
    })
  }, [salaryTimeEntries])

  const { exportTimesheets: exportMutation } = useTimesheetExportMutations()

  const exportConfig: YearMonthExportData = {
    companyId: company.id,
    month: filter.month.getMonth(),
    year: filter.month.getFullYear(),
    userId: selectedUserId,
    mailId: me.id,
  }

  async function handleExport(options: ExportForm, salaryDates: [Date, Date]) {
    try {
      setExportLoading(true)
      const startMonthYearTime = startOfMonth(filter.month)
      const endMonthYearTime = endOfMonth(startMonthYearTime)
      const useSalaryDates = options.exportType === ExportType.ENTRY_SALARY
      const firstOfMonth = formatISO(
        useSalaryDates ? startOfDay(salaryDates[0]) : startMonthYearTime
      )
      const lastOfMonth = formatISO(
        useSalaryDates ? endOfDay(salaryDates[1]) : endMonthYearTime
      )

      await exportMutation({
        variables: {
          userMail: exportConfig.mailId!,
          user: exportConfig.userId,
          company: exportConfig.companyId,
          dateStart: firstOfMonth,
          dateEnd: lastOfMonth,
          exportType: options.exportType,
          exportFormat: options.exportFormat,
          exportOption: options.exportOption,
        },
      })
    } finally {
      setExportLoading(false)
      setExportModalOpen(false)
    }
  }

  const columns: Column[] = useMemo(() => {
    let columns = [
      { name: 'date', displayName: translations.date, type: '' },
      { name: 'startTime', displayName: translations.dayStart, type: '' },
      { name: 'endTime', displayName: translations.dayEnd, type: '' },
      {
        name: 'totalWork',
        displayName: company.useOrdinaryHourCalculation
          ? translations.ordinary
          : translations.hours,
        type: '',
      },
    ]
    if (!salaryModuleActive) {
      columns = [
        ...columns,
        { name: 'pause', displayName: translations.pause, type: '' },
        { name: 'overtime', displayName: translations.overtime, type: '' },
      ]
    }
    if (!isMobile) {
      columns = [
        ...columns,
        { name: 'salaryCode', displayName: translations.salaryCode, type: '' },
        { name: 'department', displayName: translations.department, type: '' },
      ]

      if (projectActive) {
        columns = [
          ...columns,
          { name: 'project', displayName: translations.facility, type: '' },
        ]
      }
    }
    columns = [
      ...columns,
      {
        name: 'latestAction',
        displayName: translations.latestAction,
        type: '',
      },
      { name: 'comment', displayName: translations.comment, type: '' },
    ]
    return columns
  }, [
    company.useOrdinaryHourCalculation,
    projectActive,
    salaryModuleActive,
    translations,
  ])

  return (
    <ModuleWrapper
      title={translations.timeEntries}
      page={PageType.TIME_ENTRIES}
      headerRight={
        <Toggle
          value={filter.view === TimeEntryViewEnum.monthly}
          iconMode={IconMode.textAndIcon}
          leftText={translations.toggle.weekly}
          rightText={translations.toggle.monthly}
          offIconProps={{
            icon: 'calendar-week',
            ...iconProps,
            color: !(filter.view === TimeEntryViewEnum.monthly)
              ? 'white'
              : undefined,
          }}
          onIconProps={{
            icon: 'calendar-alt',
            ...iconProps,
            color:
              filter.view === TimeEntryViewEnum.monthly ? 'white' : undefined,
          }}
          thumbBackground="secondary"
          background="white"
          height="3.875rem"
          width={160}
          onChange={value =>
            updateForm({
              view: value
                ? TimeEntryViewEnum.monthly
                : TimeEntryViewEnum.weekly,
            })
          }
        />
      }
    >
      {admin && (
        <ExportModal
          open={exportModalOpen}
          exportConfig={exportConfig}
          userId={filter.user}
          loading={exportLoading}
          onClose={() => setExportModalOpen(false)}
          onSubmit={handleExport}
        />
      )}

      <TimesheetControls
        filter={filter}
        leaderGroupsInfo={{
          groupLoading: groupsLoading,
          leaderGroupFilter: hasAccess,
          leaderGroups: myLeaderGroups,
        }}
        onOpenExportModal={() => setExportModalOpen(true)}
        onUpdate={updateForm}
      />

      {salaryModuleActive && !allTimeEntriesLoading && !!salaryWorkTime && (
        <>
          {filter.view === TimeEntryViewEnum.monthly ? (
            salaryTimeEntries.length > 0 ? (
              <SalaryTimeEntriesTable
                entries={salaryTimeEntries}
                salaryWorkTime={salaryWorkTime}
                columns={columns}
                projectActive={projectActive}
                stickyHeader
              />
            ) : (
              <></>
            )
          ) : (
            <Weeks>
              {weeklySalaryTables.map(periodReport => (
                <React.Fragment key={periodReport.key}>
                  <Header>
                    <h2>
                      {translations.weekN({ n: periodReport.weekNumber })}
                    </h2>
                  </Header>
                  <SalaryTimeEntriesTable
                    entries={periodReport.entries}
                    salaryWorkTime={salaryWorkTime}
                    columns={columns}
                    projectActive={projectActive}
                  />
                </React.Fragment>
              ))}
            </Weeks>
          )}
        </>
      )}

      {!salaryModuleActive && !loading && !!salaryWorkTime && (
        <>
          {filter.view === TimeEntryViewEnum.monthly ? (
            periodReports[0] ? (
              <TimesheetsTable
                periodReport={periodReports[0]}
                salaryWorkTime={salaryWorkTime}
                startDate={datetimeStartLt}
                endDate={datetimeEndGte}
                columns={columns}
                projectActive={projectActive}
                stickyHeader
              />
            ) : (
              <></>
            )
          ) : (
            <Weeks>
              {periodReports.map(periodReport => (
                <React.Fragment key={periodReport.periodStart}>
                  <Header>
                    <h2>
                      {translations.weekN({
                        n: getWeek(parseISO(periodReport.periodStart)),
                      })}
                    </h2>
                  </Header>
                  <TimesheetsTable
                    periodReport={periodReport}
                    salaryWorkTime={salaryWorkTime}
                    startDate={datetimeStartLt}
                    endDate={datetimeEndGte}
                    columns={columns}
                    projectActive={projectActive}
                  />
                </React.Fragment>
              ))}
            </Weeks>
          )}
        </>
      )}
    </ModuleWrapper>
  )
}
